
import { defineComponent } from 'vue';
import RegistrationRequestPanel from '@/components/registration/RegistrationRequestPanel.vue';
import Button from '@/components/atoms/Button.vue';
import Input from '@/components/atoms/Input.vue';
import { mapActions, mapGetters } from 'vuex';
import { redirectToCallbackURL, googleSignIn, facebookSignIn, appleSignIn, postMessageToParent, isOtpRequired } from '@/service/cognitoService';
import { InputValidator } from '@/utils';
import Message from '@/components/atoms/Message.vue';
import GoogleButton from '@/components/atoms/GoogleButton.vue';
import FacebookButton from '@/components/atoms/FacebookButton.vue';
import AppleButton from '@/components/atoms/AppleButton.vue';
import Logo from '@/components/atoms/Logo.vue';
import PasswordInput from '@/components/atoms/PasswordInput.vue';
import { CognitoException } from '@/model/cognito';
import { AppStatus } from '@/model/status';
import { MessageType, AlertMessage } from '@/model/message';
import { ROUTES_NAMES, SOCIAL_PROVIDERS } from '@/constants';

export default defineComponent({
  name: 'LoginPanel',
  components: {
    RegistrationRequestPanel,
    Button,
    Input,
    Message,
    GoogleButton,
    FacebookButton,
    AppleButton,
    Logo,
    PasswordInput
  },
  data () {
    return {
      event: {
        socialLogin: 'social_login'
      },
      password: '',
      email: '',
      loginTryCount: parseInt(sessionStorage.getItem('loginTryCount') ?? '0'),
      message: {} as AlertMessage | undefined,
      appStatusToMessageMap:
        this.initAppStatusToMessageMap() as Map<AppStatus, AlertMessage>,
      cognitoErrorsToMessageMap:
        this.initCognitoErrorsToMessageMap() as Map<CognitoException, AlertMessage>,
      showValidationErrors: false
    };
  },
  computed: {
    ...mapGetters([
      'global',
      'signInMessages',
      'signInUtils',
      'myEmail',
      'appStatus',
      'isIframe',
      'idToken',
      'accessToken',
      'refreshToken'
    ]),
    linkColor (): string {
      return this.global.linkColor || this.global.primaryColor;
    },
    enableSignInButton (): boolean {
      return InputValidator.validateEmail(this.email) && !!this.password;
    },
    checkEmailError (): boolean {
      return this.email ? !InputValidator.validateEmail(this.email) : false;
    },
    emailIsValid (): boolean {
      return this.email ? InputValidator.validateEmail(this.email) : false;
    },
    emailFieldError (): boolean {
      return (this.showValidationErrors && !this.email) || this.checkEmailError;
    },
    emailErrorMessage (): string {
      if (!this.emailFieldError) {
        return '';
      }

      return this.email
        ? this.formatLoginErrorMessage(this.$t('message.accessibility.email_invalid') as string)
        : this.formatLoginErrorMessage(this.$t('message.accessibility.email_required') as string);
    },
    passwordFieldError (): boolean {
      return this.showValidationErrors && !this.password;
    },
    passwordErrorMessage (): string {
      if (!this.passwordFieldError) {
        return '';
      }

      return this.formatLoginErrorMessage(this.$t('message.accessibility.password_required') as string);
    }
  },
  mounted () {
    if (this.myEmail) {
      this.email = this.myEmail;
      this.setMyEmail('');
    }
    if (this.appStatus) {
      this.message = this.appStatusToMessageMap.get(this.appStatus) as AlertMessage;
      this.setAppStatus('');
    }
  },
  methods: {
    ...mapActions(['signIn', 'setMyEmail', 'setAppStatus', 'startLoading', 'endLoading']),

    formatLoginErrorMessage (text: string): string {
      return text.startsWith('* ') ? text : `* ${text}`;
    },

    onSignInPressWithValidation (): void {
      this.showValidationErrors = true;

      if (!this.email) {
        return;
      }

      if (this.checkEmailError || !this.password) {
        return;
      }

      this.showValidationErrors = false;
      this.onSignInPress();
    },

    initAppStatusToMessageMap (): Map<AppStatus, AlertMessage> {
      return new Map([
        [AppStatus.UserAlreadyExists, {
          type: MessageType.Error,
          text: this.$t('message.user_already_exists_error')
        }],
        [AppStatus.ConfirmSignUpSuccess, {
          type: MessageType.Success,
          text: this.$t('message.confirmation_sign_up_success_message')
        }],
        [AppStatus.ConfirmSignUpError, {
          type: MessageType.Error,
          text: this.$t('message.confirmation_sign_up_error_message')
        }],
        [AppStatus.ResetPasswordSuccess, {
          type: MessageType.Success,
          text: this.$t('message.reset_password_success')
        }],
        [AppStatus.ResetPasswordError, {
          type: MessageType.Error,
          text: this.$t('message.reset_password_error')
        }]
      ]);
    },

    initCognitoErrorsToMessageMap (): Map<CognitoException, AlertMessage> {
      return new Map([
        [CognitoException.UsernameExistsException, {
          type: MessageType.Error,
          text: this.$t('message.sign_in_user_not_confirmed_error')
        }],
        [CognitoException.UserLambdaValidationException, {
          type: MessageType.Error,
          text: this.$t('message.sign_in_try_later_error')
        }],
        [CognitoException.NotAuthorizedException, {
          type: MessageType.Error,
          text: this.$t('message.sign_in_not_authorized_error')
        }],
        [CognitoException.UserNotConfirmedException, {
          type: MessageType.Error,
          text: this.$t('message.sign_in_user_not_confirmed_error')
        }],
        [CognitoException.UserNotFoundException, {
          type: MessageType.Error,
          text: this.$t('message.sign_in_user_not_found_error')
        }]
      ]);
    },

    async onSignInPress (): Promise<void> {
      try {
        this.message = undefined;
        this.startLoading();
        await this.signIn({
          email: this.email.toLowerCase(),
          password: this.password
        });
        sessionStorage.removeItem('loginTryCount');

        const otpStepRequired = await isOtpRequired();
        if(otpStepRequired) {
          this.switchToOTP();
          return;
        }

        this.isIframe
          ? postMessageToParent({
            idToken: this.idToken,
            accessToken: this.accessToken,
            refreshToken: this.refreshToken
          })
          : redirectToCallbackURL();
      } catch (error: any) {
        this.endLoading();
        if (error.code === CognitoException.PasswordResetRequiredException) {
          this.setAppStatus(AppStatus.ResetPasswordRequired);
          this.switchToForgotPassword();
          return;
        }
        this.loginTryCount++;
        sessionStorage.setItem('loginTryCount', this.loginTryCount.toString());
        if(this.loginTryCount > 3) {
          this.message = {
            type: MessageType.Error,
            text: this.$t('message.sign_in_3_time_limit')
          };
          return;
        }
        this.message = this.cognitoErrorsToMessageMap.get(error.code) as AlertMessage;
      }
    },
    async handleGoogleSignIn (): Promise<void> {
      try {
        this.startLoading();
        if (this.isIframe) {
          return postMessageToParent({
            event: this.event.socialLogin,
            provider: SOCIAL_PROVIDERS.GOOGLE
          });
        }
        await googleSignIn();
      } catch (error) {
      } finally {
        this.endLoading();
      }
    },
    async handleFacebookSignIn (): Promise<void> {
      try {
        this.startLoading();
        if (this.isIframe) {
          return postMessageToParent({
            event: this.event.socialLogin,
            provider: SOCIAL_PROVIDERS.FACEBOOK
          });
        }
        await facebookSignIn();
      } catch (error) {
      } finally {
        this.endLoading();
      }
    },
    async handleAppleSignIn (): Promise<void> {
      try {
        this.startLoading();
        if (this.isIframe) {
          return postMessageToParent({
            event: this.event.socialLogin,
            provider: SOCIAL_PROVIDERS.APPLE
          });
        }
        await appleSignIn();
      } catch (error) {
      } finally {
        this.endLoading();
      }
    },
    switchToForgotPassword (): void {
      if (this.email) {
        this.setMyEmail(this.email);
      }
      this.$router.push({
        path: 'forgotpassword',
        query: {
          ...this.$route.query
        }
      });
    },
    switchToOTP (): void {
      this.$router.push({
        path: ROUTES_NAMES.otp,
        query: {
          ...this.$route.query
        }
      });
    }
  }
});
